import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { Breadcrumbs } from '@aeg-presents/shared-components/Breadcrumbs';
import { Button } from '@aeg-presents/shared-components/Button';
import { DropdownButton } from '@aeg-presents/shared-components/DropdownButton';
import { PageHeader } from '@aeg-presents/shared-components/PageHeader';
import { Tabs, type TabItem } from '@aeg-presents/shared-components/Tabs';
import { useTicketingSalesStore } from '@stores/ticketingSales';
import { Loading } from '../../components/Loading';
import { RouterLink } from '../../components/RouterLink';
import { TicketSalesGrid } from './TicketSalesGrid/TicketSalesGrid';
import { TOUR_TICKET_SALES_QUERY } from '@gql/queries/tourTicketSales';
import { Tour } from '../../types/tour';
import { TourDetailsPageContainer, TourDetailsTabContent } from './TourDetailsPage.styled';
import { mapTourTicketSalesToStore } from './TourDetailsPage.utils';

enum TourActionsMenuOption {
  Action = 'Action',
}

const tourActionOptions = [{ label: 'Action', value: TourActionsMenuOption.Action }];

const TOUR_HEADER_NAME_COL_SIZE = 2;
const TOUR_HEADER_FIELDS_COL_SIZE = 7;
const TOUR_HEADER_ACTIONS_COL_SIZE = 3;

function mapTourToFields(tour: Tour) {
  return [
    { title: 'Headliner', value: tour.headliner },
    { title: 'Co-Headliners', value: tour.coHeadliners },
    { title: 'Company', value: tour.company },
    { title: 'Multi-Leg Tour', value: tour.multiLegTour },
    { title: 'Buyer', value: tour.buyer },
    { title: 'Operator', value: tour.operator },
  ];
}

export function TourDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { data, error, loading } = useQuery(TOUR_TICKET_SALES_QUERY, {
    skip: !id,
    variables: { tourId: id ?? '' },
  });
  const initializeTicketingSales = useTicketingSalesStore((state) => state.ticketingSales.initializeTicketingSales);
  const tour = useTicketingSalesStore((state) => state.ticketingSales.tourInformation);
  const tourTicketSales = data?.tourTicketSales;

  useEffect(() => {
    if (tourTicketSales) {
      initializeTicketingSales(mapTourTicketSalesToStore(tourTicketSales));
    }
  }, [initializeTicketingSales, tourTicketSales]);

  if (loading) {
    return <Loading />;
  }

  if (error || !tourTicketSales) {
    return (
      <p role="alert" data-testid="tour-details-error">
        Unable to load tour details.
      </p>
    );
  }

  if (!tour) {
    return <Loading />;
  }

  const tabs: TabItem[] = [
    {
      id: 'Ticketing',
      label: 'Ticketing',
      visible: true,
      disabled: false,
      content: (
        <TourDetailsTabContent>
          <TicketSalesGrid />
        </TourDetailsTabContent>
      ),
    },
  ];

  const handleTourAction = (_value: TourActionsMenuOption) => {
    // Placeholder until tour action flows are wired.
  };

  return (
    <TourDetailsPageContainer data-testid="tour-details">
      <Breadcrumbs
        items={[
          { text: 'Tours', href: '/tours' },
          { text: tour.name, href: `/tours/${tour.id}` },
        ]}
        LinkComponent={RouterLink}
      />
      <PageHeader
        headerText={tour.name}
        fields={mapTourToFields(tour)}
        showActions
        nameColSize={TOUR_HEADER_NAME_COL_SIZE}
        fieldsColSize={TOUR_HEADER_FIELDS_COL_SIZE}
        actionsColSize={TOUR_HEADER_ACTIONS_COL_SIZE}
        actions={
          <>
            <DropdownButton
              label="Actions"
              dropdownOptions={tourActionOptions}
              onSelect={handleTourAction}
              dataTestID="tour-header-actions"
            />
            <Button variant="contained" color="primary" dataTestID="tour-header-save">
              Save
            </Button>
          </>
        }
      />
      <Tabs tabs={tabs} idPrefix="tour-details" />
    </TourDetailsPageContainer>
  );
}
